import { toEnumFromString } from '../../serialization/enum-utils.js';
import { MapUtils } from '../../utility/map-utils.js';
import { SerializationContext, SerializedData, DataModelBuilder } from '../dto/data-model.js';
import { isSameList, isSameData } from '../dto/data-model-utils.js';
import { PaymentAccountRef } from './payment-account-ref.js';
import { PaymentType } from './payment-type.js';
import { PaymentProtocolBase } from './payment-protocol-base.js';
import { PaymentAccount } from './payment-account.js';

export class PaymentProtocol extends PaymentProtocolBase {
  static readonly TYPE_ID: string = '20b26b2661634636afb700ab480db55f';

  private constructor(
    public readonly paymentAccount: PaymentAccountRef,
    public readonly paymentMethods: PaymentType[],
    public readonly allowLegalEntityInvoices: boolean,
    public readonly allowOfflineOrderReceipts: boolean,
  ) {
    super();
  }

  get $typeId() {
    return PaymentProtocol.TYPE_ID;
  }

  static create(args: {
    paymentAccount: PaymentAccountRef,
    paymentMethods: PaymentType[],
    allowLegalEntityInvoices: boolean,
    allowOfflineOrderReceipts: boolean,
  }): PaymentProtocol {
    return new PaymentProtocol(
        args.paymentAccount,
        args.paymentMethods,
        args.allowLegalEntityInvoices,
        args.allowOfflineOrderReceipts,
    );
  }

  static fromPaymentAccount(paymentAccount: PaymentAccount): PaymentProtocol {
    return new PaymentProtocol(
        paymentAccount.toRef(),
        [PaymentType.Online],
        paymentAccount.invoicingProtocol?.allowLegalEntities ?? false,
        false,
    );
  }

  static fromMap(context: SerializationContext, map: SerializedData): PaymentProtocol {
    return new PaymentProtocol(
        context.fromData<PaymentAccountRef>(MapUtils.getRecord(map, 'paymentAccount')),
        MapUtils.getList<PaymentType>(
            map,
            'paymentMethods',
            (value) => toEnumFromString(value as string, PaymentType) ?? PaymentType.OnDelivery
        ),
        MapUtils.getBoolean(map, 'allowLegalEntityInvoices'),
        MapUtils.getBoolean(map, 'allowOfflineOrderReceipts'),
    );
  }

  toMap(context: SerializationContext): SerializedData {
    return {
      'paymentAccount': context.toData(this.paymentAccount),
      'paymentMethods': this.paymentMethods,
      'allowLegalEntityInvoices': this.allowLegalEntityInvoices,
      'allowOfflineOrderReceipts': this.allowOfflineOrderReceipts,
    };
  }

  clone(): this {
    return new PaymentProtocol(
        this.paymentAccount,
        this.paymentMethods.map((e) => e),
        this.allowLegalEntityInvoices,
        this.allowOfflineOrderReceipts,
    ) as this;
  }

  isSame(other: PaymentProtocol): boolean {
    if(this === other) {
      return true;
    }

    return isSameList(other.paymentMethods, this.paymentMethods) &&
        isSameData(other.paymentAccount, this.paymentAccount) &&
        other.allowLegalEntityInvoices === this.allowLegalEntityInvoices &&
        other.allowOfflineOrderReceipts === this.allowOfflineOrderReceipts;
  }
}

export class PaymentProtocolBuilder extends DataModelBuilder<PaymentProtocol> {
  build(context: SerializationContext, map: SerializedData): PaymentProtocol {
    return PaymentProtocol.fromMap(context, map);
  }
}
